import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { type IAddress } from '@mahawi/eshop-common/src/types';
import { Button, Space, Tag, Typography } from 'antd';
import React from 'react';
import { useSelector } from 'react-redux';

import { getCountryString } from '../fragments/get-country-string';
import { normalizeEmail } from '../fragments/normalize-email';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { emailValidatorVerify } from '../reducers/email-validator/reducer';
import { SPACING } from '../theme/tokens';
import CopyToClipboard from './copy-to-clipboard';
import GoogleMapsButton from './google-maps-button';
import OpenMapButton from './open-map-button';

const { Text } = Typography;

interface AddressDisplayProps {
  address: IAddress;
  title?: string;
  showMap?: boolean;
}

function AddressDisplay({
  address,
  title,
  showMap = true,
}: AddressDisplayProps): React.ReactElement {
  const dispatch = useAppDispatch();
  const normalizedEmail: string = normalizeEmail(address.email);
  const correlationId = `addressDisplay.${normalizedEmail}`;
  const verifyEntry = useSelector(
    (s: RootState) => s.EmailValidator.entries[correlationId],
  );
  const verifyInProcess: boolean = verifyEntry?.inProcess ?? false;
  const verifyResult = verifyEntry?.result;

  const handleVerifyEmail = (): void => {
    if (address.email) {
      dispatch(emailValidatorVerify(correlationId, address.email));
    }
  };

  const showVerifyResult =
    verifyResult !== undefined &&
    normalizeEmail(verifyResult.email) === normalizedEmail;

  return (
    <Space orientation="vertical" size={SPACING.xs}>
      {title && <Text strong>{title}</Text>}

      <Space size={SPACING.xs} align="baseline">
        <Text>name:</Text>
        <Text strong>{address.name}</Text>
        <CopyToClipboard text={address.name} type="link" />
        {showMap && <GoogleMapsButton address={address} />}
        {showMap && <OpenMapButton address={address} />}
      </Space>

      <Space size={SPACING.xs} align="baseline">
        <Text>surname:</Text>
        <Text strong>{address.surname}</Text>
        <CopyToClipboard text={address.surname} type="link" />
      </Space>

      {address.companyName && (
        <Space size={SPACING.xs} align="baseline">
          <Text>company:</Text>
          <Text strong>{address.companyName}</Text>
          <CopyToClipboard text={address.companyName} type="link" />
        </Space>
      )}

      <Space size={SPACING.xs} align="baseline">
        <Text>street:</Text>
        <Text strong>{address.street}</Text>
        <CopyToClipboard text={address.street} type="link" />
      </Space>

      <Space size={SPACING.xs} align="baseline">
        <Text>street number:</Text>
        <Text strong>{address.streetNumber}</Text>
        {address.streetNumber && (
          <CopyToClipboard text={address.streetNumber} type="link" />
        )}
      </Space>

      <Space size={SPACING.xs} align="baseline">
        <Text>city:</Text>
        <Text strong>{address.city}</Text>
        <CopyToClipboard text={address.city} type="link" />
      </Space>

      <Space size={SPACING.xs} align="baseline">
        <Text>zip:</Text>
        <Text strong>{address.zip}</Text>
        {address.zip && <CopyToClipboard text={address.zip} type="link" />}
      </Space>

      <Space size={SPACING.xs} align="baseline">
        <Text>country:</Text>
        <Text strong>{getCountryString(address.countryTypeIsoCode)}</Text>
        {address.countryTypeIsoCode && (
          <CopyToClipboard text={address.countryTypeIsoCode} type="link" />
        )}
      </Space>

      <Space size={SPACING.xs} align="baseline">
        <Text>phone:</Text>
        <Text strong>{address.phone}</Text>
        {address.phone && <CopyToClipboard text={address.phone} type="link" />}
      </Space>

      <Space size={SPACING.xs} align="baseline">
        <Text>email:</Text>
        <Text strong>{address.email}</Text>
        {address.email && <CopyToClipboard text={address.email} type="link" />}
        {address.email && (
          <Button
            size="small"
            loading={verifyInProcess}
            disabled={verifyInProcess}
            onClick={handleVerifyEmail}
          >
            Verify email
          </Button>
        )}
      </Space>

      {showVerifyResult && (
        <Space wrap size={SPACING.xs}>
          {verifyResult.isValid === true && (
            <Tag color="success" icon={<CheckCircleOutlined />}>
              Valid
            </Tag>
          )}
          {verifyResult.isValid === false && (
            <Tag color="error" icon={<CloseCircleOutlined />}>
              Invalid
            </Tag>
          )}
          {verifyResult.isValid === null && (
            <Tag color="warning" icon={<WarningOutlined />}>
              Inconclusive
            </Tag>
          )}
          {verifyResult.isDisposable && <Tag color="warning">Disposable</Tag>}
          {verifyResult.suggestedEmail && (
            <Tag color="processing">
              Did you mean &quot;{verifyResult.suggestedEmail}&quot;?
            </Tag>
          )}
        </Space>
      )}
    </Space>
  );
}

export default AddressDisplay;
